<script setup lang="ts">
import { computed } from 'vue';
import {
  highlightSearchSegments,
  searchHighlightMarkClass,
} from '../utils/search-highlight';
import {
  trackerPickerDeviceId,
  trackerPickerDeviceImei,
  trackerPickerDeviceName,
  trackerPickerDeviceSerial,
  type TrackerPickerDevice,
} from '../utils/tracker-picker';

const props = withDefaults(
  defineProps<{
    device: TrackerPickerDevice;
    searchQuery?: string;
    selected?: boolean;
  }>(),
  {
    searchQuery: '',
    selected: false,
  },
);

const searchTrim = computed(() => props.searchQuery.trim());
const markClass = computed(() => searchHighlightMarkClass(props.selected));
</script>

<template>
  <div class="flex min-w-0 flex-col justify-center gap-0.5 py-0.5">
    <p
      class="truncate text-sm font-medium"
      :class="selected ? 'text-gpx_terracotta-100' : 'text-gray-900'"
    >
      <template
        v-for="(part, hi) in highlightSearchSegments(
          trackerPickerDeviceName(device),
          searchTrim,
        )"
        :key="`dn-${device.id}-${hi}`"
      >
        <mark v-if="part.hl" :class="markClass">{{ part.text }}</mark>
        <template v-else>{{ part.text }}</template>
      </template>
    </p>
    <p
      class="truncate font-mono text-xs"
      :class="selected ? 'text-gpx_terracotta-100/80' : 'text-gpx_gray-200'"
    >
      Serial:
      <template
        v-for="(part, hi) in highlightSearchSegments(
          trackerPickerDeviceSerial(device),
          searchTrim,
        )"
        :key="`ds-${device.id}-${hi}`"
      >
        <mark v-if="part.hl" :class="markClass">{{ part.text }}</mark>
        <template v-else>{{ part.text }}</template>
      </template>
      <span class="mx-1.5 opacity-50" aria-hidden="true">·</span>
      IMEI:
      <template
        v-for="(part, hi) in highlightSearchSegments(
          trackerPickerDeviceImei(device),
          searchTrim,
        )"
        :key="`di-${device.id}-${hi}`"
      >
        <mark v-if="part.hl" :class="markClass">{{ part.text }}</mark>
        <template v-else>{{ part.text }}</template>
      </template>
      <span class="mx-1.5 opacity-50" aria-hidden="true">·</span>
      ID:
      <template
        v-for="(part, hi) in highlightSearchSegments(
          trackerPickerDeviceId(device),
          searchTrim,
        )"
        :key="`did-${device.id}-${hi}`"
      >
        <mark v-if="part.hl" :class="markClass">{{ part.text }}</mark>
        <template v-else>{{ part.text }}</template>
      </template>
    </p>
  </div>
</template>
